import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { withDateRange } from '@/helpers/dateRangeFilename';

// Downloads a multi-sheet XLSX of org usage data — per-matter and per-user stats plus
// weekly series for pages, runs, chronologies, Kim threads and Kim queries.
export const useDownloadUsageData = () => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadUsageData = async ({
    organisationId,
    startDate,
    endDate,
  }: {
    organisationId: string;
    startDate?: Date;
    endDate?: Date;
  }) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: { organisationId, startDate, endDate },
    });

    const response = await fetch(`${APIBaseChronos}/api/usage/download`, fetchConfig);
    if (!response.ok) {
      throw new Error('Failed to download usage data');
    }

    const blob = await response.blob();
    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: withDateRange('wexler-usage-data', 'xlsx', startDate, endDate),
    });
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const { mutate: fetchDownload, isLoading } = useMutation(downloadUsageData, {
    onSuccess: () => {
      trackCustomEvent(TrackingEventsV2.UsageDashboardDownloadData);
    },
    onError: () => {
      toast.error('Failed to download usage data. Please try again.');
    },
  });

  return { fetchDownload, isLoading };
};
